/**
 * TagsField — the one tags editor used across the Design tab (Levels, Screens, section
 * items). Comma-separated text that commits a parsed string[] on blur/Enter, so typing
 * "fire, " doesn't fight a controlled value. With `suggestions`, the token being typed
 * autocompletes from the design's known tags (↑↓ + Enter/Tab, click).
 */
import { useEffect, useRef, useState } from 'react';
import { parseTagList } from './lib/tags';
import { filterTagSuggestions } from './lib/suggest';

export function TagsField({
  tags,
  onCommit,
  placeholder = 'fire, hazard, level-3 (comma-separated)',
  compact = false,
  suggestions,
}: {
  tags: string[];
  onCommit: (tags: string[]) => void;
  placeholder?: string;
  compact?: boolean;
  /** Known tags across the design (most-used first) — enables autocomplete. */
  suggestions?: string[];
}): React.ReactElement {
  const joined = tags.join(', ');
  const [text, setText] = useState(joined);
  const [focused, setFocused] = useState(false);
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => setText(joined), [joined]);

  const commit = (): void => {
    const next = parseTagList(text);
    if (next.join('|') !== tags.join('|')) onCommit(next);
  };

  // Autocomplete the token after the last comma.
  const cut = text.lastIndexOf(',');
  const settled = parseTagList(cut === -1 ? '' : text.slice(0, cut + 1));
  const currentToken = cut === -1 ? text : text.slice(cut + 1);
  const matches =
    focused && suggestions ? filterTagSuggestions(currentToken, settled, suggestions) : [];
  const open = matches.length > 0;

  const accept = (tag: string): void => {
    const prefix = cut === -1 ? '' : `${text.slice(0, cut + 1).trimEnd()} `;
    setText(`${prefix}${tag}, `);
    setIndex(0);
    inputRef.current?.focus();
  };

  return (
    <label
      className={`relative flex min-w-0 items-center gap-2 ${compact ? 'text-[11px]' : 'text-xs'} text-crucible-muted`}
    >
      Tags
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setIndex(0);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onKeyDown={(e) => {
          if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            const delta = e.key === 'ArrowDown' ? 1 : -1;
            setIndex((i) => (i + delta + matches.length) % matches.length);
          } else if (open && (e.key === 'Tab' || (e.key === 'Enter' && currentToken.trim()))) {
            // Tab and mid-token Enter both complete; Enter on a settled list still commits.
            e.preventDefault();
            accept(matches[index]);
          } else if (e.key === 'Escape' && open) {
            setFocused(false);
          } else if (e.key === 'Enter') {
            commit();
            (e.target as HTMLInputElement).blur();
          }
          e.stopPropagation();
        }}
        placeholder={placeholder}
        className={`min-w-0 flex-1 rounded border border-crucible-border bg-transparent px-2 ${
          compact ? 'py-0.5 text-[11px]' : 'py-1 text-xs'
        } text-crucible-fg outline-none focus:border-crucible-accent`}
      />
      {open && (
        <div
          data-testid="tags-suggest-popup"
          className="app-region-no-drag absolute left-10 right-0 top-full z-40 mt-1 overflow-hidden rounded-md border border-crucible-border bg-crucible-surface shadow-xl"
          // Keep the input focused (and its blur-commit at bay) while clicking.
          onMouseDown={(e) => e.preventDefault()}
        >
          {matches.map((tag, i) => (
            <button
              key={tag}
              type="button"
              onClick={() => accept(tag)}
              onMouseEnter={() => setIndex(i)}
              className={`block w-full px-2.5 py-1 text-left ${
                i === index ? 'bg-crucible-accent/15 text-crucible-fg' : 'text-crucible-fg'
              }`}
            >
              #{tag}
            </button>
          ))}
        </div>
      )}
    </label>
  );
}
